import { ApiBase } from '@bymason/api-base';
import {
  GenerateDeviceReportOptions,
  GenerateDeviceReportResult,
  GetHardwareOptions,
  GetJobsStatusOptions,
  HardwareModel,
  JobsStatusModel,
} from './types';

export * from './types';

class Tracker extends ApiBase {
  private static instance: Tracker;

  private constructor() {
    super();
  }

  /**
   * There is no need for consumers to ever call this method as its purely used to enforce a singleton module pattern.
   * @returns A Tracker instance
   */
  public static getInstance(): Tracker {
    if (!Tracker.instance) {
      Tracker.instance = new Tracker();
      Tracker.instance.configure({
        legacy: true,
      });
    }

    return Tracker.instance;
  }

  /**
   * Gets an object of hardware information.
   * @param options metadata required to get hardware info.
   * @param clientToken token required for proper authorization.
   * @returns either a project model or void
   */
  public async getHardware(options: GetHardwareOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('tracker/hardware');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<HardwareModel>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets jobs status list.
   * @param options metadata required to get jobs status list.
   * @param clientToken token required for proper authorization.
   * @returns either void or a pageable structure that includes projects.
   */
  public async getJobsStatus(options: GetJobsStatusOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('tracker/builder/:customerId/jobs');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<JobsStatusModel>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Initializes device report creation and sends to email address.
   * @param options metadata required to generate a device report.
   * @param clientToken token required for proper authorization.
   * @returns either void or a pageable structure that includes projects.
   */
  public async generateDeviceReport(options: GenerateDeviceReportOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('tracker/devices/:customerId/@device-report?address=:email');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<GenerateDeviceReportResult>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }
}

export const trackerClient = Tracker.getInstance();
